import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { useCountry } from '@/contexts/CountryContext';

const DestinationMap: React.FC = () => {
  const { destinations, content, label } = useCountry();
  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  const regions = content.mapRegions.map((r) => ({
    id: r.id,
    name: r.name,
    destinations: destinations.filter((d) => r.match.includes(d.region)),
  }));

  const activeRegionData = regions.find((r) => r.id === activeRegion);

  return (
    <section className="py-24 lg:py-32 bg-[#F5F1E8]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Explore the Regions
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            {content.mapTitle}
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-xl mx-auto leading-relaxed">
            {content.mapIntro}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* Map Image */}
          <div className="lg:col-span-3">
            <div className="relative bg-[#FAF3E4] border border-[#C9A961]/20 p-4 md:p-6 shadow-sm">
              <img
                src={content.mapImage}
                alt={content.mapImageAlt}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="text-center font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mt-6">
              {content.mapCaption}
            </p>
          </div>

          {/* Region Details Panel */}
          <div className="lg:col-span-2">
            <h3 className="font-serif text-2xl text-[#1A3B5C] font-light mb-6">
              Explore by Region
            </h3>
            <div className="space-y-3 mb-8">
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() =>
                    setActiveRegion(activeRegion === region.id ? null : region.id)
                  }
                  className={`w-full text-left flex items-center justify-between px-5 py-4 border transition-all duration-300 ${
                    activeRegion === region.id
                      ? 'border-[#C9A961] bg-[#C9A961]/10'
                      : 'border-[#1A3B5C]/15 hover:border-[#C9A961]/60 bg-white/40'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-3 h-3 rounded-full border ${
                        activeRegion === region.id
                          ? 'bg-[#C9A961] border-[#C9A961]'
                          : 'border-[#1A3B5C]/40'
                      }`}
                    />
                    <span className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C]">
                      {region.name}
                    </span>
                  </div>
                  <span className="font-sans text-[10px] text-[#999]">
                    {region.destinations.length}
                  </span>
                </button>
              ))}
            </div>

            {activeRegionData && activeRegionData.destinations.length > 0 ? (
              <div className="animate-fade-in border-t border-[#C9A961]/20 pt-6">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-5">
                  {activeRegionData.destinations.length}{' '}
                  {activeRegionData.destinations.length === 1
                    ? 'Destination'
                    : 'Destinations'}
                </p>
                <div className="space-y-5 max-h-[420px] overflow-y-auto pr-2">
                  {activeRegionData.destinations.map((dest) => (
                    <div
                      key={dest.id}
                      className="flex gap-4 group cursor-pointer"
                      onClick={() => {
                        const el = document.getElementById('destinations');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <div className="w-24 h-20 flex-shrink-0 overflow-hidden">
                        <img
                          src={dest.image}
                          alt={dest.alt || `${dest.name} in ${dest.region}, ${label}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-serif text-lg text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors truncate">
                          {dest.name}
                        </h4>
                        <div className="flex items-center gap-1 mb-2">
                          <MapPin className="w-3 h-3 text-[#C9A961]" />
                          <span className="font-sans text-[10px] text-[#999] truncate">
                            {dest.location}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = document.getElementById('plan-journey');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="font-sans text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="border-t border-[#C9A961]/20 pt-6">
                <p className="font-sans text-xs text-[#777] leading-relaxed">
                  {content.mapEmptyText}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DestinationMap;
